import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Grid, Header, Divider } from '@mairistem/composants';

import { PanelButtonEdition } from '@mairistem/administres-utils';
import ShowDiffContent from '@/components/Common/ShowDiffContent';
import { useUpdateConvention } from '@/hooks';
import formatPhoneNumber from '@/utils/formatPhoneNumber';
import { formaterNaissance } from '@/utils/dateNaissance';
import {
  formatLieu,
  formatPrenoms,
  hasRepresentantsLegaux,
  toNumeroVoie,
} from '@/utils';
import { OPTIONS_ETAT_MATRIMONIAL } from '@/constants';

import AutresInformationsModal from './AutresInformationsModal';

// Code INSEE -> libellé, pour tenir à jour côté client le libellé affiché
// en lecture seule sans attendre un rechargement du pacs.
export const etatMatrimonialLibelle = (code: string | null): string | null =>
  (OPTIONS_ETAT_MATRIMONIAL.find((option) => option.value === code)
    ?.text as string) ?? null;

// Adresse de résidence complète du partenaire : numéro de voie + libellé de
// voie + codePostal - LOCALITE (majuscules) (pays).
const formatAdresseResidence = (partenaire: any): string => {
  const voie = [
    toNumeroVoie(partenaire?.residenceNumeroVoirie),
    partenaire?.residenceLibelleVoie,
  ]
    .map((value) => (value ?? '').toString().trim())
    .filter(Boolean)
    .join(' ');

  const localiteCodePostal = formatLieu(
    partenaire?.residenceLocalite,
    partenaire?.residenceCodePostal,
    partenaire?.residencePays,
    '',
  );

  return (
    [voie, localiteCodePostal].filter(Boolean).join(' ') || 'Non renseigné'
  );
};

// "Oui"/"Non" (renvoyés par le GET convention) -> booléen pour les radios
const ouiNonToBoolean = (value: any): boolean | null => {
  if (value === 'Oui' || value === true || value === 1) return true;
  if (value === 'Non' || value === false || value === 0) return false;
  return null;
};

// booléen -> smallint (0/1) pour le payload persisté en base
const booleanToInt = (value: boolean | null): number | null => {
  if (value === true) return 1;
  if (value === false) return 0;
  return null;
};

// Le back renvoie ces champs tantôt en "Oui"/"Non", tantôt en smallint 0/1
// selon qu'ils ont été modifiés via cette fiche ou non : on normalise
// l'affichage en lecture seule pour ne jamais montrer la valeur brute.
const ouiNonLibelle = (value: any): string | null => {
  const bool = ouiNonToBoolean(value);
  if (bool === true) return 'Oui';
  if (bool === false) return 'Non';
  return null;
};

// Valeurs INSEE courantes du partenaire (préfixe p1/p2), utilisées à la fois
// pour initialiser le brouillon d'édition et pour détecter les changements.
const complementInseePacs = (complementInsee: any, prefix: 'p1' | 'p2') => ({
  etatMatrimonialCode:
    complementInsee?.[`${prefix}EtatMatrimonialCode`] ?? null,
  dateDivorce: complementInsee?.[`${prefix}DateDivorce`] ?? null,
  dateVeuvage: complementInsee?.[`${prefix}DateVeuvage`] ?? null,
  dejaPacse: ouiNonLibelle(complementInsee?.[`${prefix}DejaPacse`]),
  nombreEnfants: complementInsee?.nombreEnfants ?? null,
});

import InformationsPartenaireNaissanceModal from './InformationsPartenaireNaissanceModal';
import InformationsPartenaireResidenceModal from './InformationsPartenaireResidenceModal';

import InformationsParentModal from './InformationsParentModal';

interface InformationsPartenaireProps {
  showDiff?: boolean;
  partenaire: any;
  previousPartenaire?: any;
  perePartenaire?: any;
  previousPerePartenaire?: any;
  merePartenaire?: any;
  previousMerePartenaire?: any;
  EtatMatrimonial?: string;
  etatMatrimonialCode?: string;
  dateDivorce?: string;
  dateVeuvage?: string;
  dejaPacse?: string;
  nombreEnfants?: number;
  showAllInsee?: boolean;
  inseeObligatoire?: boolean;
  dataInseeOld?: boolean;
  numeroPartenaire?: number;
  dateInitialeInsee?: string | null;
  idConvention?: number | string;
  partenaireKey?: 'partenaire1' | 'partenaire2';
  conventionDeclaration?: any;
  complementInsee?: any;
  identifiantPacs?: number;
  onSaved?: () => void;
  onComplementInseeSaved?: (complementInsee: any) => void;
  updated?: boolean;
  onChange?: (data: Record<string, unknown>) => void;
  onSave?: () => Promise<void>;
  onRollback?: () => void;
}

const InformationsPartenaire = (
  props: InformationsPartenaireProps,
): JSX.Element => {
  const {
    showDiff = false,
    partenaire,
    previousPartenaire,
    perePartenaire,
    previousPerePartenaire,
    merePartenaire,
    previousMerePartenaire,
    EtatMatrimonial,
    etatMatrimonialCode,
    dateDivorce,
    dateVeuvage: dateVeuvageInsee,
    dejaPacse,
    nombreEnfants,
    showAllInsee,
    inseeObligatoire = false,
    dataInseeOld,
    numeroPartenaire,
    dateInitialeInsee,
    idConvention,
    partenaireKey,
    conventionDeclaration,
    complementInsee,
    identifiantPacs,
    onSaved,
    onComplementInseeSaved,
    updated,
    onChange,
    onSave,
    onRollback,
  } = props;

  const { updateConvention } = useUpdateConvention();

  const inseePrefix: 'p1' | 'p2' =
    partenaireKey === 'partenaire2' ? 'p2' : 'p1';

  // Édition d'un parent (père = « PARENT 1 », mère = « PARENT 2 ») : brouillon
  // local à plat, persisté par un PUT de la convention complète à la sauvegarde.
  const [parentModalOpen, setParentModalOpen] = React.useState<
    'pere' | 'mere' | null
  >(null);
  const [parentDraft, setParentDraft] = React.useState<any>(null);

  const handleOpenParent = React.useCallback(
    (type: 'pere' | 'mere') => {
      setParentDraft(type === 'pere' ? perePartenaire : merePartenaire);
      setParentModalOpen(type);
    },
    [perePartenaire, merePartenaire],
  );

  const handleChangeParent = React.useCallback(
    (data: Record<string, unknown>) => {
      setParentDraft((prev: any) => ({ ...prev, ...data }));
    },
    [],
  );

  // si déjà transférée à l'INSEE, les champs INSEE
  // (état matrimonial + dates, déjà pacsé, nombre d'enfants) ne sont plus
  // affichés dans cette modale.
  const hideInseeFields = !!dateInitialeInsee;

  const [autresInfosModalOpen, setAutresInfosModalOpen] =
    React.useState<boolean>(false);
  const [naissanceModalOpen, setNaissanceModalOpen] =
    React.useState<boolean>(false);
  const [residenceModalOpen, setResidenceModalOpen] =
    React.useState<boolean>(false);

  const [autresInfoTemp, setAutresInfoTemp] = React.useState<{
    protectionJuridique: boolean | null;
    protectionOfpra: boolean | null;
    identiteVerifiee: boolean | null;
    etatMatrimonialCode: string | null;
    dateDivorce: string | null;
    dateVeuvage: string | null;
    dejaPacse: string | null;
    nombreEnfants: number | null;
  }>({
    protectionJuridique: null,
    protectionOfpra: null,
    identiteVerifiee: null,
    etatMatrimonialCode: null,
    dateDivorce: null,
    dateVeuvage: null,
    dejaPacse: null,
    nombreEnfants: null,
  });

  const handleOpenAutresInfos = React.useCallback(() => {
    setAutresInfoTemp({
      protectionJuridique: ouiNonToBoolean(partenaire?.protectionJuridique),
      protectionOfpra: ouiNonToBoolean(partenaire?.protectionOfpra),
      identiteVerifiee: ouiNonToBoolean(partenaire?.identiteVerifiee),
      ...complementInseePacs(complementInsee, inseePrefix),
    });
    setAutresInfosModalOpen(true);
  }, [partenaire, complementInsee, inseePrefix]);

  const handleCloseAutresInfos = React.useCallback(() => {
    setAutresInfosModalOpen(false);
  }, []);

  const handleChangeAutresInfos = React.useCallback(
    (data: {
      protectionJuridique?: boolean | null;
      protectionOfpra?: boolean | null;
      identiteVerifiee?: boolean | null;
      etatMatrimonialCode?: string | null;
      dateDivorce?: string | null;
      dateVeuvage?: string | null;
      dejaPacse?: string | null;
      nombreEnfants?: number | null;
    }) => {
      setAutresInfoTemp((prev) => ({ ...prev, ...data }));
    },
    [],
  );

  const handleCloseParent = React.useCallback(() => {
    setParentModalOpen(null);
    setParentDraft(null);
  }, []);

  const handleSaveParent = React.useCallback(() => {
    if (!idConvention || !numeroPartenaire || !conventionDeclaration) {
      return Promise.reject(
        new Error('Contexte de convention incomplet pour la sauvegarde'),
      );
    }

    const key =
      parentModalOpen === 'pere'
        ? `perePartenaire${numeroPartenaire}`
        : `merePartenaire${numeroPartenaire}`;

    const payload = {
      ...conventionDeclaration,
      [key]: {
        ...conventionDeclaration[key],
        ...parentDraft,
      },
    };

    return updateConvention(idConvention, payload).then(() => {
      onSaved?.();
    });
  }, [
    idConvention,
    numeroPartenaire,
    conventionDeclaration,
    parentModalOpen,
    parentDraft,
    updateConvention,
    onSaved,
  ]);

  const handleSaveAutresInfos = React.useCallback(() => {
    if (!idConvention || !partenaireKey || !conventionDeclaration) {
      return Promise.reject(new Error('Convention introuvable'));
    }

    const nextComplementInsee = {
      ...complementInsee,
      identifiantPacs: complementInsee?.identifiantPacs ?? identifiantPacs,
      [`${inseePrefix}EtatMatrimonialCode`]: autresInfoTemp.etatMatrimonialCode,
      [`${inseePrefix}EtatMatrimonialLibelle`]: etatMatrimonialLibelle(
        autresInfoTemp.etatMatrimonialCode,
      ),
      [`${inseePrefix}DateDivorce`]: autresInfoTemp.dateDivorce,
      [`${inseePrefix}DateVeuvage`]: autresInfoTemp.dateVeuvage,
      [`${inseePrefix}DejaPacse`]: autresInfoTemp.dejaPacse,
      ...(showAllInsee && { nombreEnfants: autresInfoTemp.nombreEnfants }),
    };

    const payload = {
      ...conventionDeclaration,
      [partenaireKey]: {
        ...conventionDeclaration[partenaireKey],
        protectionJuridique: booleanToInt(autresInfoTemp.protectionJuridique),
        protectionOfpra: booleanToInt(autresInfoTemp.protectionOfpra),
        identiteVerifiee: booleanToInt(autresInfoTemp.identiteVerifiee),
      },
      complementInsee: nextComplementInsee,
    };

    return updateConvention(idConvention, payload).then(() => {
      // pacs.complementInsee n'est pas rechargé automatiquement (le PUT
      // porte sur la convention, pas sur le pacs) : on pousse la valeur
      // enregistrée au container pour que lecture et modale restent à jour.
      onComplementInseeSaved?.(nextComplementInsee);
      onSaved?.();
    });
  }, [
    complementInsee,
    conventionDeclaration,
    autresInfoTemp,
    idConvention,
    identifiantPacs,
    inseePrefix,
    onComplementInseeSaved,
    onSaved,
    partenaireKey,
    showAllInsee,
    updateConvention,
  ]);
  const handleCloseNaissance = React.useCallback(() => {
    setNaissanceModalOpen(false);
  }, []);

  const handleCancelNaissance = React.useCallback(() => {
    onRollback?.();
    handleCloseNaissance();
  }, [handleCloseNaissance, onRollback]);

  const handleCloseResidence = React.useCallback(() => {
    setResidenceModalOpen(false);
  }, []);

  const handleCancelResidence = React.useCallback(() => {
    onRollback?.();
    handleCloseResidence();
  }, [handleCloseResidence, onRollback]);
  // pour éviter d'avoir des 'Invald Date' dans le cas ou la date passé est null
  // Selon la précision enregistrée : JJ/MM/AAAA, MM/AAAA ou AAAA
  const formatNaissanceDate = (individu?: {
    naissanceDate?: string | null;
    naissanceTypeDate?: string | number | null;
  }): string =>
    formaterNaissance(individu?.naissanceDate, individu?.naissanceTypeDate);

  // Les informations INSEE sont toujours affichées, même non renseignées
  const formatValeurInsee = (
    value?: string | number | null,
  ): string | number => {
    if (!dataInseeOld) return 'Donnée épurée';
    return _.isNil(value) || value === '' ? 'Non renseigné' : value;
  };

  const showDateEtatMatrimonial =
    etatMatrimonialCode === 'V' || etatMatrimonialCode === 'D';
  const inseeDateRaw =
    etatMatrimonialCode === 'V' ? dateVeuvageInsee : dateDivorce;
  const inseeDateAffichee = inseeDateRaw
    ? dayjs(inseeDateRaw).format('DD/MM/YYYY')
    : null;

  const lieuResidence = formatAdresseResidence(partenaire);
  const prevLieuResidence = formatAdresseResidence(previousPartenaire);

  const lieuNaissance = formatLieu(
    partenaire?.naissanceLocalite,
    partenaire?.naissanceCodePostal,
    partenaire?.naissancePays,
  );
  const prevLieuNaissance = formatLieu(
    previousPartenaire?.naissanceLocalite,
    previousPartenaire?.naissanceCodePostal,
    previousPartenaire?.naissancePays,
  );

  const lieuNaissancePere = formatLieu(
    perePartenaire?.naissanceLocalite,
    perePartenaire?.naissanceCodePostal,
    perePartenaire?.naissancePays,
  );
  const prevLieuNaissancePere = formatLieu(
    previousPerePartenaire?.naissanceLocalite,
    previousPerePartenaire?.naissanceCodePostal,
    previousPerePartenaire?.naissancePays,
  );

  const lieuNaissanceMere = formatLieu(
    merePartenaire?.naissanceLocalite,
    merePartenaire?.naissanceCodePostal,
    merePartenaire?.naissancePays,
  );
  const prevLieuNaissanceMere = formatLieu(
    previousMerePartenaire?.naissanceLocalite,
    previousMerePartenaire?.naissanceCodePostal,
    previousMerePartenaire?.naissancePays,
  );

  const isRepresentantsLegaux = hasRepresentantsLegaux({
    protectionJuridique: ouiNonToBoolean(partenaire?.protectionJuridique),
    protectionOfpra: ouiNonToBoolean(partenaire?.protectionOfpra),
  });
  const titreParent1 = isRepresentantsLegaux
    ? 'INFORMATIONS DU REPRÉSENTANT LÉGAL 1'
    : 'INFORMATIONS DU PARENT 1';
  const titreParent2 = isRepresentantsLegaux
    ? 'INFORMATIONS DU REPRÉSENTANT LÉGAL 2'
    : 'INFORMATIONS DU PARENT 2';

  return (
    <Grid>
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            INFORMATIONS DE NAISSANCE
          </Header>
          <PanelButtonEdition
            onClick={() => setNaissanceModalOpen(true)}
            data-testid="panel-edit-modal-partenaire-naissance"
          />
        </div>
        {naissanceModalOpen && (
          <InformationsPartenaireNaissanceModal
            partenaire={partenaire}
            updated={updated ?? false}
            onChange={onChange ?? (() => {})}
            onSave={onSave ?? (() => Promise.resolve())}
            onClose={handleCloseNaissance}
            onCancel={handleCancelNaissance}
          />
        )}
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={3}>
          {showDiff ? (
            <>
              <span>
                <b>Né le</b>
              </span>
              <ShowDiffContent
                oldContent={formatNaissanceDate(previousPartenaire)}
                newContent={formatNaissanceDate(partenaire)}
              />
            </>
          ) : (
            <>
              <span>
                <b>Né le : </b>
              </span>
              <span>{formatNaissanceDate(partenaire)}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={2}>
          {showDiff ? (
            <>
              <span>
                <b>Sexe</b>
              </span>
              <ShowDiffContent
                oldContent={previousPartenaire?.sexeCode || 'Non renseigné'}
                newContent={partenaire?.sexeCode}
              />
            </>
          ) : (
            <>
              <span>
                <b>Sexe : </b>
              </span>
              <span>{partenaire?.sexeCode}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Nom (nom d&apos;usage)</b>
              </span>
              <ShowDiffContent
                oldContent={`${previousPartenaire?.nomFamille || 'Non renseigné'} ${previousPartenaire?.nomUsage ? `(${previousPartenaire?.nomUsage})` : ''}`}
                newContent={`${partenaire?.nomFamille || 'Non renseigné'} ${partenaire?.nomUsage ? `(${partenaire?.nomUsage})` : ''}`}
              />
            </>
          ) : (
            <>
              <span>
                <b>Nom (nom d&apos;usage) : </b>
              </span>
              <span>
                {partenaire?.nomFamille || 'Non renseigné'}{' '}
                {partenaire?.nomUsage && `(${partenaire?.nomUsage})`}
              </span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Prénom(s)</b>
              </span>
              <ShowDiffContent
                oldContent={formatPrenoms(
                  previousPartenaire?.prenom || 'Non renseigné',
                  previousPartenaire?.prenomsComplementaires,
                )}
                newContent={formatPrenoms(
                  partenaire?.prenom || 'Non renseigné',
                  partenaire?.prenomsComplementaires,
                )}
              />
            </>
          ) : (
            <>
              <span>
                <b>Prénom(s) : </b>
              </span>
              <span>
                {formatPrenoms(
                  partenaire?.prenom,
                  partenaire?.prenomsComplementaires,
                ) || 'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Lieu de naissance</b>
              </span>
              <ShowDiffContent
                oldContent={prevLieuNaissance}
                newContent={lieuNaissance}
              />
            </>
          ) : (
            <>
              <span>
                <b>Lieu de naissance : </b>
              </span>
              <span>{lieuNaissance}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Commune déléguée</b>
              </span>
              <ShowDiffContent
                oldContent={
                  previousPartenaire?.naissanceLibelleCommuneDelegue ||
                  'Non renseignée'
                }
                newContent={
                  partenaire?.naissanceLibelleCommuneDelegue || 'Non renseignée'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Commune déléguée : </b>
              </span>
              <span>
                {partenaire?.naissanceLibelleCommuneDelegue || 'Non renseignée'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Nationalité 1</b>
              </span>
              <ShowDiffContent
                oldContent={
                  previousPartenaire?.premiereNationalite || 'Non renseignée'
                }
                newContent={partenaire?.premiereNationalite || 'Non renseignée'}
              />
            </>
          ) : (
            <>
              <span>
                <b>Nationalité 1 : </b>
              </span>
              <span>{partenaire?.premiereNationalite || 'Non renseignée'}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Nationalité 2</b>
              </span>
              <ShowDiffContent
                oldContent={
                  previousPartenaire?.deuxiemeNationalite || 'Non renseignée'
                }
                newContent={partenaire?.deuxiemeNationalite || 'Non renseignée'}
              />
            </>
          ) : (
            <>
              <span>
                <b>Nationalité 2 : </b>
              </span>
              <span>{partenaire?.deuxiemeNationalite || 'Non renseignée'}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Nationalité 3</b>
              </span>
              <ShowDiffContent
                oldContent={
                  previousPartenaire?.troisiemeNationalite || 'Non renseignée'
                }
                newContent={
                  partenaire?.troisiemeNationalite || 'Non renseignée'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Nationalité 3 : </b>
              </span>
              <span>
                {partenaire?.troisiemeNationalite || 'Non renseignée'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            ADRESSE DE RÉSIDENCE ET CONTACT
          </Header>
          <PanelButtonEdition
            onClick={() => setResidenceModalOpen(true)}
            data-testid="panel-edit-modal-partenaire-residence"
          />
        </div>
        {residenceModalOpen && (
          <InformationsPartenaireResidenceModal
            partenaire={partenaire}
            updated={updated ?? false}
            onChange={onChange ?? (() => {})}
            onSave={onSave ?? (() => Promise.resolve())}
            onClose={handleCloseResidence}
            onCancel={handleCancelResidence}
          />
        )}
      </Grid.Row>
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'flex-start',
            width: '100%',
            gap: '1em',
            paddingLeft: '1rem',
            paddingRight: '1rem',
          }}
        >
          {/* 31% car sinon affichage de commune déléguée n'est pas aligné */}
          <div style={{ flex: '0 1 31%', whiteSpace: 'nowrap' }}>
            {showDiff ? (
              <>
                <span>
                  <b>Adresse de résidence</b>
                </span>
                <ShowDiffContent
                  oldContent={prevLieuResidence}
                  newContent={lieuResidence}
                />
              </>
            ) : (
              <>
                <span>
                  <b>Adresse de résidence : </b>
                </span>
                <span>{lieuResidence}</span>
              </>
            )}
          </div>
          <div style={{ flex: '0 0 31.25%' }}>
            {showDiff ? (
              <>
                <span>
                  <b>Commune déléguée</b>
                </span>
                <ShowDiffContent
                  oldContent={
                    previousPartenaire?.residenceLibelleCommuneDelegue ||
                    'Non renseigné'
                  }
                  newContent={
                    partenaire?.residenceLibelleCommuneDelegue ||
                    'Non renseigné'
                  }
                />
              </>
            ) : (
              <>
                <span>
                  <b>Commune déléguée : </b>
                </span>
                <span>
                  {partenaire?.residenceLibelleCommuneDelegue ||
                    'Non renseigné'}
                </span>
              </>
            )}
          </div>
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Téléphone 1</b>
              </span>
              <ShowDiffContent
                oldContent={
                  formatPhoneNumber(previousPartenaire?.telephone1) ||
                  'Non renseigné'
                }
                newContent={
                  formatPhoneNumber(partenaire?.telephone1) || 'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Téléphone 1 : </b>
              </span>
              <span>
                {formatPhoneNumber(partenaire?.telephone1) || 'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Téléphone 2</b>
              </span>
              <ShowDiffContent
                oldContent={
                  formatPhoneNumber(previousPartenaire?.telephone2) ||
                  'Non renseigné'
                }
                newContent={
                  formatPhoneNumber(partenaire?.telephone2) || 'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Téléphone 2 : </b>
              </span>
              <span>
                {formatPhoneNumber(partenaire?.telephone2) || 'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Adresse mail</b>
              </span>
              <ShowDiffContent
                oldContent={previousPartenaire?.courriel || 'Non renseigné'}
                newContent={partenaire?.courriel || 'Non renseigné'}
              />
            </>
          ) : (
            <>
              <span>
                <b>Adresse mail : </b>
              </span>
              <span>{partenaire?.courriel || 'Non renseigné'}</span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            AUTRES INFORMATIONS
          </Header>
          <PanelButtonEdition
            onClick={handleOpenAutresInfos}
            data-testid="panel-edit-modal-partenaire-autres-infos"
          />
        </div>
      </Grid.Row>
      {autresInfosModalOpen && (
        <AutresInformationsModal
          protectionJuridique={autresInfoTemp.protectionJuridique}
          protectionOfpra={autresInfoTemp.protectionOfpra}
          identiteVerifiee={autresInfoTemp.identiteVerifiee}
          etatMatrimonialCode={autresInfoTemp.etatMatrimonialCode}
          dateDivorce={autresInfoTemp.dateDivorce}
          dateVeuvage={autresInfoTemp.dateVeuvage}
          dejaPacse={autresInfoTemp.dejaPacse}
          nombreEnfants={autresInfoTemp.nombreEnfants}
          showAllInsee={showAllInsee}
          hideInseeFields={hideInseeFields}
          inseeObligatoire={inseeObligatoire}
          radioGroupSuffix={partenaireKey}
          onChange={handleChangeAutresInfos}
          onSave={handleSaveAutresInfos}
          onClose={handleCloseAutresInfos}
          onCancel={handleCloseAutresInfos}
        />
      )}
      <Grid.Row>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>
                  Placé(e) sous la protection juridique et adminsitrative de
                  l&apos;OFPRA
                </b>
              </span>
              <ShowDiffContent
                oldContent={
                  ouiNonLibelle(previousPartenaire?.protectionOfpra) ||
                  'Non renseigné'
                }
                newContent={
                  ouiNonLibelle(partenaire?.protectionOfpra) || 'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>
                  Placé(e) sous la protection juridique et adminsitrative de
                  l&apos;OFPRA :{' '}
                </b>
              </span>
              <span>
                {ouiNonLibelle(partenaire?.protectionOfpra) || 'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Fait l'objet d'une mesure de protection juridique : </b>
              </span>
              <ShowDiffContent
                oldContent={
                  ouiNonLibelle(previousPartenaire?.protectionJuridique) ||
                  'Non renseigné'
                }
                newContent={
                  ouiNonLibelle(partenaire?.protectionJuridique) ||
                  'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Fait l'objet d'une mesure de protection juridique : </b>
              </span>
              <span>
                {ouiNonLibelle(partenaire?.protectionJuridique) ||
                  'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>A fourni un titre d&apos;identité vérifié</b>
              </span>
              <ShowDiffContent
                oldContent={
                  ouiNonLibelle(previousPartenaire?.identiteVerifiee) ||
                  'Non renseigné'
                }
                newContent={
                  ouiNonLibelle(partenaire?.identiteVerifiee) || 'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>A fourni un titre d&apos;identité vérifié : </b>
              </span>
              <span>
                {ouiNonLibelle(partenaire?.identiteVerifiee) || 'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          <span>
            <b>Etat matrimonial antérieur (INSEE) : </b>
          </span>
          <span>{formatValeurInsee(EtatMatrimonial)}</span>
        </Grid.Column>
        {showDateEtatMatrimonial && (
          <Grid.Column width={5}>
            <span>
              <b>
                {etatMatrimonialCode === 'V'
                  ? 'Date de veuvage (INSEE) : '
                  : 'Date de divorce (INSEE) : '}
              </b>
            </span>
            <span>{formatValeurInsee(inseeDateAffichee)}</span>
          </Grid.Column>
        )}
        <Grid.Column width={5}>
          <span>
            <b>Partenaire déjà pacsé par le passé (INSEE) : </b>
          </span>
          <span>{formatValeurInsee(ouiNonLibelle(dejaPacse))}</span>
        </Grid.Column>
      </Grid.Row>
      {showAllInsee && (
        <Grid.Row>
          <Grid.Column width={7}>
            <span>
              <b>
                Nombre d&apos;enfants en commun avec le partenaire 2 (INSEE) :{' '}
              </b>
            </span>
            <span>{formatValeurInsee(nombreEnfants)}</span>
          </Grid.Column>
        </Grid.Row>
      )}
      <Divider />
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            {titreParent1}
          </Header>
          <PanelButtonEdition
            onClick={() => handleOpenParent('pere')}
            data-testid="panel-edit-modal-partenaire-pere"
          />
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={3}>
          {showDiff ? (
            <>
              <span>
                <b>Né le</b>
              </span>
              <ShowDiffContent
                oldContent={formatNaissanceDate(previousPerePartenaire)}
                newContent={formatNaissanceDate(perePartenaire)}
              />
            </>
          ) : (
            <>
              <span>
                <b>Né le : </b>
              </span>
              <span>{formatNaissanceDate(perePartenaire)}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={2}>
          {showDiff ? (
            <>
              <span>
                <b>Sexe</b>
              </span>
              <ShowDiffContent
                oldContent={previousPerePartenaire?.sexeCode || 'Non renseigné'}
                newContent={perePartenaire?.sexeCode || 'Non renseigné'}
              />
            </>
          ) : (
            <>
              <span>
                <b>Sexe : </b>
              </span>
              <span>{perePartenaire?.sexeCode || 'Non renseigné'}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Nom (nom d&apos;usage)</b>
              </span>
              <ShowDiffContent
                oldContent={`${previousPerePartenaire?.nomFamille || 'Non renseigné'} ${previousPerePartenaire?.nomUsage ? `(${previousPerePartenaire?.nomUsage})` : ''}`}
                newContent={`${perePartenaire?.nomFamille || 'Non renseigné'} ${perePartenaire?.nomUsage ? `(${perePartenaire?.nomUsage})` : ''}`}
              />
            </>
          ) : (
            <>
              <span>
                <b>Nom (nom d&apos;usage) : </b>
              </span>
              <span>
                {perePartenaire?.nomFamille || 'Non renseigné'}{' '}
                {perePartenaire?.nomUsage && `(${perePartenaire?.nomUsage})`}
              </span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Prénom(s)</b>
              </span>
              <ShowDiffContent
                oldContent={formatPrenoms(
                  previousPerePartenaire?.prenom || 'Non renseigné',
                  previousPerePartenaire?.prenomsComplementaires,
                )}
                newContent={formatPrenoms(
                  perePartenaire?.prenom || 'Non renseigné',
                  perePartenaire?.prenomsComplementaires,
                )}
              />
            </>
          ) : (
            <>
              <span>
                <b>Prénom(s) : </b>
              </span>
              <span>
                {formatPrenoms(
                  perePartenaire?.prenom || 'Non renseigné',
                  perePartenaire?.prenomsComplementaires,
                )}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Lieu de naissance</b>
              </span>
              <ShowDiffContent
                oldContent={prevLieuNaissancePere}
                newContent={lieuNaissancePere}
              />
            </>
          ) : (
            <>
              <span>
                <b>Lieu de naissance : </b>
              </span>
              <span>{lieuNaissancePere}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Commune déléguée</b>
              </span>
              <ShowDiffContent
                oldContent={
                  previousPerePartenaire?.naissanceLibelleCommuneDelegue ||
                  'Non renseigné'
                }
                newContent={
                  perePartenaire?.naissanceLibelleCommuneDelegue ||
                  'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Commune déléguée : </b>
              </span>
              <span>
                {perePartenaire?.naissanceLibelleCommuneDelegue ||
                  'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            {titreParent2}
          </Header>
          <PanelButtonEdition
            onClick={() => handleOpenParent('mere')}
            data-testid="panel-edit-modal-partenaire-mere"
          />
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={3}>
          {showDiff ? (
            <>
              <span>
                <b>Né le</b>
              </span>
              <ShowDiffContent
                oldContent={formatNaissanceDate(previousMerePartenaire)}
                newContent={formatNaissanceDate(merePartenaire)}
              />
            </>
          ) : (
            <>
              <span>
                <b>Né le : </b>
              </span>
              <span>{formatNaissanceDate(merePartenaire)}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={2}>
          {showDiff ? (
            <>
              <span>
                <b>Sexe</b>
              </span>
              <ShowDiffContent
                oldContent={previousMerePartenaire?.sexeCode || 'Non renseigné'}
                newContent={merePartenaire?.sexeCode || 'Non renseigné'}
              />
            </>
          ) : (
            <>
              <span>
                <b>Sexe : </b>
              </span>
              <span>{merePartenaire?.sexeCode || 'Non renseigné'}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Nom (nom d&apos;usage)</b>
              </span>
              <ShowDiffContent
                oldContent={`${previousMerePartenaire?.nomFamille || 'Non renseigné'} ${previousMerePartenaire?.nomUsage ? `(${previousMerePartenaire?.nomUsage})` : ''}`}
                newContent={`${merePartenaire?.nomFamille || 'Non renseigné'} ${merePartenaire?.nomUsage ? `(${merePartenaire?.nomUsage})` : ''}`}
              />
            </>
          ) : (
            <>
              <span>
                <b>Nom (nom d&apos;usage) : </b>
              </span>
              <span>
                {merePartenaire?.nomFamille || 'Non renseigné'}{' '}
                {merePartenaire?.nomUsage && `(${merePartenaire?.nomUsage})`}
              </span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Prénom(s)</b>
              </span>
              <ShowDiffContent
                oldContent={formatPrenoms(
                  previousMerePartenaire?.prenom || 'Non renseigné',
                  previousMerePartenaire?.prenomsComplementaires,
                )}
                newContent={formatPrenoms(
                  merePartenaire?.prenom || 'Non renseigné',
                  merePartenaire?.prenomsComplementaires,
                )}
              />
            </>
          ) : (
            <>
              <span>
                <b>Prénom(s) : </b>
              </span>
              <span>
                {formatPrenoms(
                  merePartenaire?.prenom || 'Non renseigné',
                  merePartenaire?.prenomsComplementaires,
                )}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Lieu de naissance</b>
              </span>
              <ShowDiffContent
                oldContent={prevLieuNaissanceMere}
                newContent={lieuNaissanceMere}
              />
            </>
          ) : (
            <>
              <span>
                <b>Lieu de naissance : </b>
              </span>
              <span>{lieuNaissanceMere}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Commune déléguée</b>
              </span>
              <ShowDiffContent
                oldContent={
                  previousMerePartenaire?.naissanceLibelleCommuneDelegue ||
                  'Non renseigné'
                }
                newContent={
                  merePartenaire?.naissanceLibelleCommuneDelegue ||
                  'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Commune déléguée : </b>
              </span>
              <span>
                {merePartenaire?.naissanceLibelleCommuneDelegue ||
                  'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      {parentModalOpen && (
        <InformationsParentModal
          parent={parentDraft}
          numeroParent={parentModalOpen === 'pere' ? 1 : 2}
          header={parentModalOpen === 'pere' ? titreParent1 : titreParent2}
          onChange={handleChangeParent}
          onSave={handleSaveParent}
          onClose={handleCloseParent}
          onCancel={handleCloseParent}
        />
      )}
    </Grid>
  );
};

export default InformationsPartenaire;
